import { useEffect } from 'react'

/**
 * useKeyboardShortcuts — Registers all keyboard shortcuts for the editor.
 *
 * Shortcuts:
 * - ⌘S / Ctrl+S  → Save
 * - ⌘Z / Ctrl+Z  → Undo
 * - ⌘Y / Ctrl+Y  → Redo
 * - ⌘Z + Shift   → Redo (alternative)
 * - ⌘D / Ctrl+D  → Duplicate selected node
 * - Escape        → Close context menu
 */
interface UseKeyboardShortcutsParams {
  handleSave: () => void;
  undo: () => void;
  redo: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedNode: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  duplicateNode: (node: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setContextMenu: (menu: any) => void;
}

export function useKeyboardShortcuts({ handleSave, undo, redo, selectedNode, duplicateNode, setContextMenu }: UseKeyboardShortcutsParams) {
  useEffect(() => {
    const mod = (e: KeyboardEvent) => e.metaKey || e.ctrlKey

    function onKey(e: KeyboardEvent) {
      if (mod(e) && e.key === 's') {
        e.preventDefault()
        handleSave()
      }
      if (mod(e) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        undo()
      }
      if (mod(e) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        redo()
      }
      if (mod(e) && e.key === 'd') {
        e.preventDefault()
        if (selectedNode) duplicateNode(selectedNode)
      }
      if (e.key === 'Escape') {
        setContextMenu(null)
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleSave, undo, redo, selectedNode, duplicateNode, setContextMenu])
}
